/**
 * KapeLessonMedia — renders KAPE-generated slide deck + narration player
 * with audio-driven slide synchronization on the student LMS lesson page.
 *
 * Phase 7D Completion Patch:
 *   · Auto-sync ON by default — slide advances as audio crosses timing
 *     thresholds recorded in `narration.timing_map`.
 *   · Manual slide selection switches to "Manual Slide Mode" — audio
 *     keeps playing but slide progression pauses.
 *   · A "Resume auto-sync" button jumps back to the slide matching the
 *     audio's current time and re-enables automatic advance.
 *   · Seeking the audio while auto-sync is on updates the slide
 *     immediately; seeking while manual mode is on preserves the
 *     student's chosen slide.
 *   · When no timing_map is available, the player degrades gracefully —
 *     manual navigation only, no auto-sync UI.
 *
 * Props:
 *   lesson       — the lesson document as returned by GET /lms/lessons/{id}
 *   enrollmentId — for the authenticated narration audio URL
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Presentation, Volume2, RefreshCw } from "lucide-react";

export default function KapeLessonMedia({ lesson, enrollmentId }) {
  const kape = lesson?.kape || {};
  const slides = Array.isArray(kape.slides)
    ? kape.slides.filter((s) => (s?.title || s?.bullets?.length || s?.image_url))
    : [];
  const narration = kape.narration || {};
  const timingMap = Array.isArray(narration.timing_map) ? narration.timing_map : [];
  const hasSlides = slides.length > 0;
  const hasAudio = !!narration.cache_id;
  if (!hasSlides && !hasAudio) return null;

  return (
    <SynchronizedPlayer
      slides={slides}
      narration={narration}
      timingMap={timingMap}
      lessonId={lesson.id}
      enrollmentId={enrollmentId}
      transcript={lesson.narration_script || kape?.narration_script || ""}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════
// Synchronized player
// ═══════════════════════════════════════════════════════════════════

function SynchronizedPlayer({ slides, narration, timingMap, lessonId, enrollmentId, transcript }) {
  const audioRef = useRef(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [manualMode, setManualMode] = useState(false);
  const [audioTime, setAudioTime] = useState(0);
  const [audioErr, setAudioErr] = useState("");
  const [showTranscript, setShowTranscript] = useState(false);
  const hasAudio = !!narration?.cache_id;

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const audioSrc = useMemo(() => {
    if (!narration.cache_id) return "";
    return (
      `${backend}/api/university/lms/narration/${narration.cache_id}` +
      `?enrollment_id=${encodeURIComponent(enrollmentId)}` +
      `&lesson_id=${encodeURIComponent(lessonId)}`
    );
  }, [backend, narration.cache_id, enrollmentId, lessonId]);

  // Build a slide_id → slide-index lookup so we can map timing entries
  // (which reference slide_id) to the array position.
  const idToIdx = useMemo(() => {
    const map = new Map();
    slides.forEach((s, i) => {
      const key = s?.id || `slide-${s?.order ?? i}`;
      map.set(key, i);
    });
    return map;
  }, [slides]);

  const timedEntries = useMemo(
    () =>
      timingMap
        .filter((t) => typeof t.start_seconds === "number")
        .sort((a, b) => a.start_seconds - b.start_seconds),
    [timingMap]
  );
  const hasTiming = timedEntries.length > 0;

  const indexForAudioTime = useCallback(
    (t) => {
      if (!hasTiming) return currentIdx;
      let match = timedEntries[0];
      for (const entry of timedEntries) {
        if (entry.start_seconds <= t) match = entry;
        else break;
      }
      const idx = idToIdx.get(match.slide_id);
      return typeof idx === "number" ? idx : 0;
    },
    [timedEntries, idToIdx, hasTiming, currentIdx]
  );

  // Audio events → auto-sync slide advance
  const onTimeUpdate = () => {
    const t = audioRef.current?.currentTime ?? 0;
    setAudioTime(t);
    if (!manualMode && hasTiming) {
      const idx = indexForAudioTime(t);
      if (idx !== currentIdx) setCurrentIdx(idx);
    }
  };
  const onSeeked = () => {
    if (!manualMode && hasTiming) {
      const idx = indexForAudioTime(audioRef.current?.currentTime ?? 0);
      setCurrentIdx(idx);
    }
  };

  const goToSlide = (i) => {
    if (i < 0 || i >= slides.length) return;
    setCurrentIdx(i);
    if (hasTiming) setManualMode(true);
  };

  const resumeAutoSync = () => {
    setManualMode(false);
    const t = audioRef.current?.currentTime ?? 0;
    const idx = indexForAudioTime(t);
    setCurrentIdx(idx);
  };

  const restart = () => {
    if (audioRef.current) audioRef.current.currentTime = 0;
    setCurrentIdx(0);
    setManualMode(false);
  };

  const current = slides[currentIdx] || {};
  const total = slides.length;

  return (
    <div className="mt-6 space-y-3" data-testid="kape-lesson-media">
      <SlideView
        slide={current}
        idx={currentIdx}
        total={total}
        onPrev={() => goToSlide(currentIdx - 1)}
        onNext={() => goToSlide(currentIdx + 1)}
        onJump={goToSlide}
        slides={slides}
      />

      {hasAudio && (
        <section
          className="rounded-2xl border border-purple-100 bg-purple-50/40 p-4"
          data-testid="kape-narration-player"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-purple-900 mb-2 flex-wrap">
            <Volume2 className="w-3.5 h-3.5" />
            <span>Lesson narration — Dr. Christy Vonta</span>
            {narration.duration_seconds > 0 && (
              <span className="ml-1 text-purple-700 normal-case tracking-normal">
                (~{Math.round(narration.duration_seconds)}s)
              </span>
            )}
            <span className="ml-auto flex items-center gap-2">
              {hasTiming ? (
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    manualMode
                      ? "bg-amber-100 text-amber-900"
                      : "bg-emerald-100 text-emerald-900"
                  }`}
                  data-testid="kape-sync-mode-label"
                >
                  {manualMode ? "Manual slide mode" : "Auto-sync on"}
                </span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid="kape-sync-unavailable">
                  Manual navigation
                </span>
              )}
              {manualMode && (
                <button
                  type="button"
                  onClick={resumeAutoSync}
                  className="text-[11px] font-semibold rounded-full px-3 py-1 bg-purple-900 text-white hover:bg-purple-800"
                  data-testid="kape-resume-autosync"
                >
                  <RefreshCw className="inline w-3 h-3 mr-1" />
                  Resume auto-sync
                </button>
              )}
              <button
                type="button"
                onClick={restart}
                className="text-[11px] rounded-full px-2 py-1 border border-purple-300 text-purple-900 hover:bg-purple-100"
                data-testid="kape-restart-playback"
              >
                Restart
              </button>
            </span>
          </div>

          {audioErr ? (
            <div className="text-sm text-slate-600" data-testid="kape-narration-error">
              Narration is temporarily unavailable. Please refresh — you can complete this lesson without the audio.
            </div>
          ) : (
            <audio
              ref={audioRef}
              controls
              preload="metadata"
              className="w-full"
              data-testid="kape-narration-audio"
              onTimeUpdate={onTimeUpdate}
              onSeeked={onSeeked}
              onError={() => setAudioErr("audio load failed")}
            >
              <source src={audioSrc} type="audio/mpeg" />
              Your browser does not support HTML5 audio.
            </audio>
          )}

          {hasTiming && (
            <div className="mt-2 text-[11px] text-purple-800 flex items-center gap-2 flex-wrap">
              <span data-testid="kape-audio-time">
                {formatSeconds(audioTime)} · slide {currentIdx + 1} of {total}
              </span>
              {manualMode && (
                <span className="text-amber-800">
                  Manual mode — the audio keeps playing but the slide will not auto-advance.
                </span>
              )}
            </div>
          )}
        </section>
      )}

      {transcript && (
        <details
          className="rounded-2xl border border-slate-200 bg-white p-4"
          onToggle={(e) => setShowTranscript(e.target.open)}
          data-testid="kape-transcript-toggle"
        >
          <summary className="text-xs uppercase tracking-wide text-slate-600 cursor-pointer">
            {showTranscript ? "Hide transcript" : "Show transcript"}
          </summary>
          <div className="mt-3 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed"
               data-testid="kape-transcript-body">
            {transcript}
          </div>
        </details>
      )}
    </div>
  );
}

function SlideView({ slide, idx, total, onPrev, onNext, onJump, slides }) {
  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
      data-testid="kape-slide-deck"
    >
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
          <Presentation className="w-3.5 h-3.5" />
          <span data-testid="kape-slide-progress">
            Slide {idx + 1} of {total}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrev}
            disabled={idx === 0}
            className="rounded-md px-2 h-8 text-sm text-slate-600 hover:bg-slate-200 disabled:opacity-40"
            data-testid="kape-slide-prev"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={idx === total - 1}
            className="rounded-md px-2 h-8 text-sm text-slate-600 hover:bg-slate-200 disabled:opacity-40"
            data-testid="kape-slide-next"
            aria-label="Next slide"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="p-6 min-h-[280px] flex flex-col justify-start" data-testid="kape-slide-body">
        {slide.image_url && slide.image_status !== "text_only_approved" && (
          <img
            src={slide.image_url}
            alt={slide.image_alt || slide.title || "Slide visual"}
            className="w-full rounded-lg mb-4 object-cover max-h-72"
            data-testid="kape-slide-image"
          />
        )}
        <h3 className="text-xl font-semibold text-slate-900 leading-tight" data-testid="kape-slide-title">
          {slide.title || "Untitled slide"}
        </h3>
        {Array.isArray(slide.bullets) && slide.bullets.length > 0 && (
          <ul className="mt-4 space-y-2 text-sm text-slate-700" data-testid="kape-slide-bullets">
            {slide.bullets.map((b, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-purple-900 mt-1">•</span>
                <span className="leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        )}
        {slide.image_caption && (
          <div className="mt-3 text-xs text-slate-500 italic" data-testid="kape-slide-caption">
            {slide.image_caption}
          </div>
        )}
      </div>

      {/* Slide dots — small screens hide these to reduce visual noise */}
      <div className="hidden sm:flex items-center justify-center gap-1.5 py-3 border-t border-slate-100">
        {slides.map((_, i) => (
          <button
            type="button"
            key={i}
            onClick={() => onJump(i)}
            className="h-1.5 rounded-full transition-all"
            style={{
              width: i === idx ? 20 : 6,
              background: i === idx ? "#5B21B6" : "#CBD5E1",
            }}
            aria-label={`Jump to slide ${i + 1}`}
            data-testid={`kape-slide-dot-${i}`}
          />
        ))}
      </div>
    </section>
  );
}

function formatSeconds(s) {
  const secs = Math.floor(s % 60);
  const mins = Math.floor(s / 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}
